import { IconButton, Select } from '@artosera/ui';
import type { Langue } from '@artosera/shared';
import { render, resoudreTheme, type SiteData } from '@artosera/site-renderer';
import { Monitor, Smartphone, X } from 'lucide-react';
import type { JSX, SyntheticEvent } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../api/errors.js';
import { useDonneesSite } from '../../api/site.js';
import { usePiegeFocus } from '../usePiegeFocus.js';
import styles from './ApercuOverlay.module.css';

export interface ApercuOverlayProps {
  galerieSlug: string;
  open: boolean;
  onClose: () => void;
  langueParDefaut: Langue;
  languesActives: readonly Langue[];
}

const BANDEAU_APERCU =
  '<div style="position:fixed;left:0;right:0;bottom:0;padding:8px 16px;' +
  'background:#161616;color:#fdfdfd;font:13px/1.4 -apple-system,BlinkMacSystemFont,' +
  '"Segoe UI",sans-serif;text-align:center;z-index:2147483647;">' +
  'Aperçu — non publié</div>';

const BALISE_FEUILLE_DE_STYLE = /<link rel="stylesheet" href="[^"]*"\s*\/?>/;

/**
 * Rend une page côté client, avec le moteur pur `render()` (D2 : « aperçu =
 * même moteur sans publier »). La feuille de style externe (`assets/<theme>.css`,
 * écrite par la publication) n'existe pas encore : elle est inlinée depuis
 * `theme.css(charte)` pour que l'`<iframe srcDoc>` se suffise à elle-même.
 */
function fabriquerHtml(donnees: SiteData, langue: Langue, chemin: string): string {
  const theme = resoudreTheme(donnees.charte.theme);
  const brut = render(donnees.charte.theme, donnees, langue, chemin);
  const style = `<style>${theme.css(donnees.charte)}</style>`;
  const avecStyle = BALISE_FEUILLE_DE_STYLE.test(brut)
    ? brut.replace(BALISE_FEUILLE_DE_STYLE, style)
    : brut.replace('</head>', `${style}</head>`);
  return avecStyle.includes('</body>')
    ? avecStyle.replace('</body>', `${BANDEAU_APERCU}</body>`)
    : `${avecStyle}${BANDEAU_APERCU}`;
}

/**
 * Aperçu plein écran (S6-f, § 7.3 D2) : bouton « Aperçu » de l'en-tête de
 * `SitePage`. Rendu côté client à partir de `GET …/site/donnees` — pas de
 * publication, sélecteur FR/EN et bureau/mobile, navigation interne en
 * interceptant les liens de la page rendue (le moteur sait rendre n'importe
 * quel chemin de son propre site, § 15.9).
 */
export function ApercuOverlay({
  galerieSlug,
  open,
  onClose,
  langueParDefaut,
  languesActives,
}: ApercuOverlayProps): JSX.Element | null {
  const { t } = useTranslation(['regie', 'commun']);
  const donneesSite = useDonneesSite(galerieSlug, open);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  usePiegeFocus(overlayRef, open, onClose);

  const [langue, setLangue] = useState<Langue>(langueParDefaut);
  const [chemin, setChemin] = useState(`/${langueParDefaut}/`);
  const [appareil, setAppareil] = useState<'bureau' | 'mobile'>('bureau');

  const html = useMemo(() => {
    if (!donneesSite.data) return null;
    return fabriquerHtml(donneesSite.data.donnees, langue, chemin);
  }, [donneesSite.data, langue, chemin]);

  if (!open) return null;

  function allerA(langueCible: Langue, cheminCible: string): void {
    setLangue(langueCible);
    setChemin(cheminCible);
  }

  function changerLangue(langueCible: Langue): void {
    // Repart de l'accueil dans la nouvelle langue : traduire le chemin
    // courant demanderait de connaître sa rubrique, ce que l'aperçu n'a pas
    // à résoudre lui-même (§ 7.3 : navigation « simple »).
    allerA(langueCible, `/${langueCible}/`);
  }

  /**
   * Intercepte les liens internes de la page rendue pour rester dans
   * l'aperçu. `clic.target` appartient au *realm* de l'iframe : un
   * `instanceof Element` comparé au `Element` de la fenêtre parente y est
   * toujours faux (deux réalms, deux constructeurs), d'où l'appel direct à
   * `closest` plutôt qu'une vérification de type.
   */
  function surChargementIframe(evenement: SyntheticEvent<HTMLIFrameElement>): void {
    const document_ = evenement.currentTarget.contentDocument;
    if (!document_) return;
    // Les touches pressées dans la page rendue ne remontent pas à la fenêtre :
    // Échap doit fermer l'aperçu aussi quand le focus est dans l'iframe.
    document_.addEventListener('keydown', (touche) => {
      if (touche.key === 'Escape') onClose();
    });
    document_.addEventListener('click', (clic) => {
      const cible = (
        clic.target as { closest?: (selecteur: string) => Element | null } | null
      )?.closest?.('a');
      const href = cible?.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('//')) return;
      clic.preventDefault();
      const segment = href.split('/')[1];
      allerA(segment === 'en' ? 'en' : 'fr', href);
    });
  }

  return (
    <div
      ref={overlayRef}
      tabIndex={-1}
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('regie:site.apercu.titre')}
    >
      <div className={styles.barre}>
        <span className={styles.titre}>{t('regie:site.apercu.titre')}</span>

        <div className={styles.controles}>
          {languesActives.length > 1 ? (
            <Select
              aria-label={t('regie:site.apercu.langue')}
              value={langue}
              onValueChange={(valeur) => changerLangue(valeur as Langue)}
              options={languesActives.map((code) => ({ value: code, label: code.toUpperCase() }))}
            />
          ) : null}

          <div
            className={styles.appareilGroupe}
            role="group"
            aria-label={t('regie:site.apercu.appareil')}
          >
            <IconButton
              variant="outline"
              size="sm"
              icon={<Monitor aria-hidden />}
              aria-label={t('regie:site.apercu.bureau')}
              aria-pressed={appareil === 'bureau'}
              className={appareil === 'bureau' ? styles.appareilActif : undefined}
              onClick={() => setAppareil('bureau')}
            />
            <IconButton
              variant="outline"
              size="sm"
              icon={<Smartphone aria-hidden />}
              aria-label={t('regie:site.apercu.mobile')}
              aria-pressed={appareil === 'mobile'}
              className={appareil === 'mobile' ? styles.appareilActif : undefined}
              onClick={() => setAppareil('mobile')}
            />
          </div>

          <IconButton
            variant="outline"
            icon={<X aria-hidden />}
            aria-label={t('commun:actions.fermer')}
            onClick={onClose}
          />
        </div>
      </div>

      <div className={styles.scene} data-appareil={appareil}>
        <div className={styles.cadre}>
          {donneesSite.isLoading ? (
            <p className={styles.etat}>{t('commun:chargement')}</p>
          ) : donneesSite.isError ? (
            <p className={styles.etat} role="alert">
              {estApiError(donneesSite.error)
                ? donneesSite.error.message
                : t('commun:erreurReseau')}
            </p>
          ) : html ? (
            <iframe
              ref={iframeRef}
              title={t('regie:site.apercu.titre')}
              className={styles.iframe}
              // `srcDoc` hérite de l'origine de la régie : sans bac à sable, un
              // script du rendu (contenu de galerie, thème) agirait avec la
              // session de l'utilisateur. `allow-same-origin` SANS
              // `allow-scripts` : aucun script ne s'y exécute, et la régie
              // garde l'accès au document pour intercepter les liens.
              sandbox="allow-same-origin"
              srcDoc={html}
              onLoad={surChargementIframe}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
